import React from 'react';
import FeaturedSVG from '../assets/FeaturedLogo.svg';
import { Stack, Typography, Box, Container, Grid } from '@mui/material';
import AboutUsPic from "../assets/AboutUsPic.webp";

const AboutUs = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        py: 8,
        backgroundColor: '#f9f9f9', // Light background to make glassy effect pop
        backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(124, 0, 31, 0.05) 0%, rgba(255, 255, 255, 0) 90%)',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              color: '#7C001F',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '32px', md: '42px' },
              fontWeight: 700,
              mb: 2,
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: '60px',
                height: '4px',
                backgroundColor: '#7C001F',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '2px'
              }
            }}
          >
            ABOUT US
          </Typography>
        </Box>

        {/* Company Profile Section - Split Layout */}
        <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: '20px',
                p: 4,
                border: '1px solid rgba(124, 0, 31, 0.1)', // Added border
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#1a1a1a',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '24px',
                  fontWeight: 600,
                  mb: 3,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
              >
                Company Profile
              </Typography>
              <Typography
                sx={{
                  color: '#444',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  lineHeight: 1.8,
                  mb: 2
                }}
              >
                Welcome to <strong>Max Weigh</strong>, your trusted partner in precision weighing solutions. We specialize in delivering a diverse range of high-quality weighing scales tailored to meet the unique needs of various industries.
              </Typography>
              <Typography
                sx={{
                  color: '#444',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  lineHeight: 1.8,
                  mb: 2
                }}
              >
                From robust platform scales to precise jewelry solutions and advanced automation systems, we cater to a wide spectrum of requirements with unmatched reliability.
              </Typography>
              <Typography
                sx={{
                  color: '#7C001F',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 600,
                  mt: 2
                }}
              >
                Discover the Max Weigh difference — where precision meets performance.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={AboutUsPic}
              alt="Max Weigh Team/Facility"
              sx={{
                width: '100%',
                height: 'auto',
                borderRadius: '20px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
            />
          </Grid>
        </Grid>

        {/* How We Work Section - Full Width Glassy Card */}
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(12px)',
            borderRadius: '24px',
            p: { xs: 3, md: 6 },
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative background element */}
          <Box
            sx={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'rgba(124, 0, 31, 0.03)',
              zIndex: 0
            }}
          />

          <Typography
            variant="h3"
            sx={{
              color: '#1a1a1a',
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '24px', md: '30px' },
              fontWeight: 700,
              textAlign: 'center',
              mb: 2,
              position: 'relative',
              zIndex: 1
            }}
          >
            How We Work With Our Clients
          </Typography>

          <Typography
            sx={{
              color: '#555',
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              position: 'relative',
              zIndex: 1
            }}
          >
            At Max Weigh, we believe that our success lies in the satisfaction of our clients. We are committed to providing a seamless customer experience through excellence in every interaction.
          </Typography>

          <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1, width: '100%', margin: 0 }}>
            {[
              { title: "Timely Delivery", desc: "We ensure your orders are delivered promptly without compromising quality." },
              { title: "Tailored Solutions", desc: "We collaborate closely to offer customized solutions that fit your specific needs." },
              { title: "Professional Installation", desc: "Our experts ensure hassle-free installation for optimal performance." },
              { title: "Comprehensive Training", desc: "We provide hands-on training to ensure your staff is confident using the equipment." },
              { title: "Timely Maintenance", desc: "Our responsive support team provides scheduled maintenance and swift repairs." },
              { title: "Transparent Communication", desc: "We keep you informed at every step, building trust through open communication." }
            ].map((item, index) => (
              <Grid item xs={12} md={6} lg={4} key={index} sx={{ paddingLeft: '32px !important', paddingTop: '32px !important' }}>
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    borderRadius: '16px',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    border: '1px solid rgba(124, 0, 31, 0.1)', // Added border
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#fff',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.05)',
                      borderColor: 'rgba(124, 0, 31, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#7C001F',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '18px',
                      fontWeight: 600,
                      mb: 1
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#444',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px',
                      lineHeight: 1.6
                    }}
                  >
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container >
    </Box >
  );
};

export default AboutUs;
